import React, { useState, useEffect, useRef, useCallback } from "react";
import "./PlaceholderPage.css";
import {
  MDBRow,
  MDBCol,
  MDBInput,
  MDBSpinner,
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBSwitch,
} from "mdb-react-ui-kit";
import { useMessageBox } from "../components/Common/MessageBox";

const API_SYNC_BASE_URL =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getUniqueMetafields";

const API_DB_BASE_URL =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getShopifyMetafieldsFromDB";

const ShopifySettingsPage = () => {
  const [metafields, setMetafields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const { showError, MessageBoxComponent } = useMessageBox();

  // POPUP STATE
  const [showPopup, setShowPopup] = useState(false);

  const pollRef = useRef(null);

  const companyId = localStorage.getItem("company_id");
  const token = localStorage.getItem("token");

  // =====================================
  // FETCH METAFIELDS FROM DB
  // =====================================
  const fetchMetafieldsFromDB = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setError("");

        if (!companyId) {
          setError("Company ID not found. Please login again.");
          return;
        }

        const res = await fetch(`${API_DB_BASE_URL}/${companyId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const result = await res.json();

        if (!res.ok || !result.success) {
          throw new Error(result.message || "Failed to fetch metafields");
        }

        setMetafields(result.data.unique_metafields || []);
        setLastSyncedAt(result.data.last_synced_at || null);
      } catch (err) {
        console.error("Fetch Error:", err);
        setError(err.message || "Something went wrong");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [companyId, token]
  );

  // =====================================
  // INITIAL LOAD
  // =====================================
  useEffect(() => {
    fetchMetafieldsFromDB();
  }, [fetchMetafieldsFromDB]);

  // =====================================
  // SYNC NOW → SHOW POPUP
  // =====================================
  const handleSyncNow = async () => {
    try {
      setError("");

      if (!companyId) {
        setError("Company ID not found. Please login again.");
        return;
      }

      const res = await fetch(`${API_SYNC_BASE_URL}/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Sync failed");
      }

      // OPEN POPUP
      setShowPopup(true);

      // START POLLING
      startPolling();
    } catch (err) {
      console.error("Sync Error:", err);
      setError(err.message || "Sync failed");
    }
  };

  // =====================================
  // POLLING
  // =====================================
  const startPolling = () => {
    if (pollRef.current) return;

    pollRef.current = setInterval(async () => {
      await fetchMetafieldsFromDB(true);
    }, 3000);
  };

  // =====================================
  // CLEANUP
  // =====================================
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  return (
    <div className="placeholder-page">
      <h1 className="page-title">Shopify Settings</h1>
      <p className="page-description">
        View all unique Shopify metafields used across your products.
      </p>

      <div className="settings-card">
        <MDBRow className="align-items-center mb-2">
          <MDBCol>
            {lastSyncedAt && (
              <small className="text-muted">
                Last synced: {new Date(lastSyncedAt).toLocaleString()}
              </small>
            )}
          </MDBCol>

          <MDBCol className="text-end">
            <MDBBtn color="primary" onClick={handleSyncNow}>
              Sync Now
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        {error && <p className="text-danger text-center">{error}</p>}

        {loading && (
          <div className="text-center my-4">
            <MDBSpinner />
            <p className="mt-2">Loading metafields...</p>
          </div>
        )}

        {!loading && metafields.length === 0 && !error && (
          <p className="text-muted text-center">
            No metafields found for this store.
          </p>
        )}

{/* Column Headings */}
<MDBRow className="mb-2 fw-bold text-center">
  <MDBCol md="3">Name</MDBCol>
  <MDBCol md="3">Key</MDBCol>
  <MDBCol md="3">Type</MDBCol>
  <MDBCol md="3">isActive</MDBCol>
</MDBRow>

{/* Metafields Rows */}
{metafields.map((mf, index) => (
  <MDBRow key={index} className="mb-3 align-items-center text-center">
    <MDBCol md="3">
      <MDBInput label="Name" value={mf.name} disabled />
    </MDBCol>
    <MDBCol md="3">
      <MDBInput label="Key" value={mf.key} disabled />
    </MDBCol>
    <MDBCol md="3">
      <MDBInput label="Type" value={mf.type} disabled />
    </MDBCol>
    <MDBCol md="3" className="d-flex justify-content-center">
      <MDBSwitch
  id={`activeSwitch-${index}`}
  checked={mf.isactive === 1} // ON agar DB me 1 ho
  label=""
  onChange={async (e) => {
    const newStatus = e.target.checked ? 1 : 0;

    try {
      // Call backend to update isactive
      const res = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/updateMetafieldIsActive`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            metafield_id: mf.id,
            isactive: newStatus,
          }),
        }
      );

      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.message);

      // Update frontend state instantly
      setMetafields((prev) =>
        prev.map((item) =>
          item.id === mf.id ? { ...item, isactive: newStatus } : item
        )
      );
    } catch (err) {
      console.error("Failed to update isActive:", err);
      showError("Failed to update isActive. Try again.");
    }
  }}
/>

    </MDBCol>
  </MDBRow>
))}




      </div>

      {/* ================= POPUP ================= */}
      <MDBModal staticBackdrop open={showPopup} setOpen={setShowPopup}>
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Sync Started</MDBModalTitle>
            </MDBModalHeader>

            <MDBModalBody className="text-center">
              Metafields sync started. You can continue working.
            </MDBModalBody>

            <MDBModalFooter>
              <MDBBtn onClick={() => setShowPopup(false)}>OK</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
      <MessageBoxComponent />
    </div>
  );
};

export default ShopifySettingsPage;
